import { PlusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Switch,
  Select,
  Breadcrumb,
} from "antd";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import GoBackButton from "../../../components/common/buttons/GoBackButton";
import { Link, useNavigate } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { createContactRequest } from "../../../api/requests/contact";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import { useGetUserRole } from "../../../api/hooks/user";

const createContactSchemaResolver = yupResolver(
  yup.object().shape({
    active: yup.boolean(),
    type: yup.string().required("Type is required"),
    name: yup.string().required("Name is required"),
    businessDetails: yup.string(),
    address: yup.object().shape({
      street: yup.string().required("Address street is required"),
      city: yup.string().required("Address city is required"),
      state: yup.string().required("Address state is required"),
      zipCode: yup.string().required("Address zipCode is required"),
      country: yup.string().required("Address country is required"),
      location: yup.object().shape({
        coordinates: yup.array().of(yup.number()),
        // .length(
        //   2,
        //   "Coordinates must have exactly two values (latitude, longitude)",
        // ),
      }),
    }),
    phone: yup
      .array()
      .of(
        yup
          .string()
          .matches(/^\d{10}$/, "Phone numbers must be exactly 10 digits"),
      )
      .min(1, "Contact must have at least one phone number")
      .required("Phone is required"),
    email: yup
      .array()
      .of(yup.string().email("Invalid email format"))
      .when("type", {
        is: (val) => val !== "CARRIER",
        then: (schema) =>
          schema
            .min(1, "Contact must have at least one email")
            .required("Email is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    customAttributes: yup.object().shape({
      size: yup
        .number()
        .positive("Size must be positive")
        .integer("Size must be an integer"),
    }),
  }),
);

function AddContactPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: userRoleList, isLoading: isLoadingUserRoleList } =
    useGetUserRole();

  const { mutateAsync: createContact } = useMutation({
    mutationFn: async (data) => {
      const res = await createContactRequest(data);
      return res.data;
    },
    mutationKey: ["contact/create"],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["contact"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(-1);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: createContactSchemaResolver,
    defaultValues: {
      active: true,
    },
  });

  console.log("errors----->", errors);
  console.log("values----->", getValues());

  async function onSubmit(data) {
    // console.log(data);
    const res = await createContact(data);
    // console.log("res----->", res);
  }

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/setup/contacts"}>Contacts</Link>,
                  },
                  {
                    title: <div className="text-lg">Create</div>,
                  },
                ]}
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
              >
                Save
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Name"
                    name="name"
                    validateStatus={errors.name ? "error" : ""}
                    help={errors.name && errors.name.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <Input {...field} placeholder="Name" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Business Details"
                    name="businessDetails"
                    validateStatus={errors.businessDetails ? "error" : ""}
                    help={
                      errors.businessDetails && errors.businessDetails.message
                    }
                  >
                    <Controller
                      control={control}
                      name="businessDetails"
                      render={({ field }) => (
                        <Input {...field} placeholder="Business Details" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Type"
                    name="type"
                    validateStatus={errors.type ? "error" : ""}
                    help={errors.type && errors.type.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="type"
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Type"
                          showSearch
                          optionFilterProp="label"
                          loading={isLoadingUserRoleList}
                          options={userRoleList?.map(({ name }) => ({
                            label: name,
                            value: name,
                          }))}
                        ></Select>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Active"
                    name="active"
                    validateStatus={errors.active ? "error" : ""}
                    help={errors.active && errors.active.message}
                  >
                    <Controller
                      control={control}
                      name="active"
                      render={({ field }) => <Switch {...field} />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Street"
                    name="address.street"
                    validateStatus={errors.address?.street ? "error" : ""}
                    help={errors.address?.street?.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address.street"
                      render={({ field }) => (
                        <Input {...field} placeholder="Street" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    name="address.city"
                    validateStatus={errors.address?.city ? "error" : ""}
                    help={errors.address?.city?.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address.city"
                      render={({ field }) => (
                        <Input {...field} placeholder="City" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="State"
                    name="address.state"
                    validateStatus={errors.address?.state ? "error" : ""}
                    help={errors.address?.state?.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address.state"
                      render={({ field }) => (
                        <Input {...field} placeholder="State" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Zip Code"
                    name="address.zipCode"
                    validateStatus={errors.address?.zipCode ? "error" : ""}
                    help={errors.address?.zipCode?.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address.zipCode"
                      render={({ field }) => (
                        <Input {...field} placeholder="Zip Code" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Country"
                    name="address.country"
                    validateStatus={errors.address?.country ? "error" : ""}
                    help={errors.address?.country?.message}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address.country"
                      render={({ field }) => (
                        <Input {...field} placeholder="Country" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Phone Numbers"
                    name="phone"
                    validateStatus={errors.phone ? "error" : ""}
                    help={
                      Array.isArray(errors.phone)
                        ? errors.phone
                            ?.filter((e) => e.message)
                            ?.map((e) => e.message)
                            .join(", ")
                        : errors.phone?.message
                    }
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="phone"
                      render={({ field }) => (
                        <Select
                          mode="tags"
                          {...field}
                          placeholder="Enter phone numbers (press Enter to add)"
                          tokenSeparators={[",", " "]}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email Addresses"
                    name="email"
                    validateStatus={errors.email ? "error" : ""}
                    help={
                      Array.isArray(errors.email)
                        ? errors.email
                            ?.filter((e) => e.message)
                            ?.map((e) => e.message)
                            .join(", ")
                        : errors.email?.message
                    }
                  >
                    <Controller
                      control={control}
                      name="email"
                      render={({ field }) => (
                        <Select
                          mode="tags"
                          {...field}
                          placeholder="Enter email addresses (press Enter to add)"
                          tokenSeparators={[",", " "]}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default AddContactPage;
